import { Component, Inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  ResolveStart,
  Router,
} from '@angular/router';
import { CbreAlertService } from '@cbre/alerts';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { AppVersionUpdateService } from '@core/services/app-version-update.service';
import { environment } from '@env/environment';
import { IMenuItem } from '@shared/models/imenu-item.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { AppStorageService, LOCALE_ID } from '@shared/services/app-storage.service';
import { OpenHelpDialogService } from '@shared/services/open-help-dialog.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { filter, map } from 'rxjs/operators';
import { MapsScriptsService } from 'src/app/maps/maps-scripts.service';
import { AlchemerSurveyService } from '@core/services/alchemer-survey.service';
import { FEATURE_TOGGLER, HybridFeatureTogglerService } from '@cbre/feature-toggles';
import { TrackSpmtNavigationService } from '@shared/track-user-actions/track-spmt-navigation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'miq-home',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AlchemerSurveyService, TrackSpmtNavigationService],
  standalone: false,
})
export class HomePageComponent implements OnDestroy, OnInit {
  public appName = environment.appName;
  public userName;
  public clipboardLength: number;
  public isRequestPage = false;
  public loading = false;
  public disableFlag = false;
  public env = environment.env;

  $isEmbedded = signal<boolean>(false);
  $isClipboard = signal<boolean>(false);

  #reporMenuItems: IMenuItem = {
    name: 'core.report-generator',
    icon: 'report-generation-rounded',
    routerLink: ['/report-generator'],
    // Report Generator App Viewer → Report Generator User
    // Report Generator App Editor → Report Generator Power User
    // Report Generator App Admin → Report Generator Admin
    permissions: ['Report Generator User', 'Report Generator Power User', 'Report Generator Admin'],
  };

  #workflowsMenuItem: IMenuItem = {
    name: 'core.workflows',
    icon: 'clipboard-icon',
    routerLink: ['/workflows'],
  };

  public menuItems: IMenuItem[] = [
    { name: 'core.records-search', icon: 'search', routerLink: ['/records'] },
    { name: 'core.reports', icon: 'reports-icon', routerLink: ['/reports'] },
    { name: 'core.queues', icon: 'queues-icon', routerLink: ['/queues'] },
    {
      name: 'core.rateNormalization',
      icon: 'market-stats-icon',
      routerLink: ['/market-setup'],
      // The user should have either Admin User or and Quarter-Close role.
      permissions: ['Admin User', 'Quarter-Close'],
    },
    // Disabling labeler. The functionality is not ready
    // { name: 'core.labeler', icon: 'label-icon', routerLink: ['/labeler'], permissions: ['Labeler User'] },
    { name: 'core.auditHistory', icon: 'audit-icon', routerLink: ['/audit-history'], permissions: ['Audit Search'] },
    { name: 'core.admin', icon: 'admin-icon', routerLink: ['/admin/users'], permissions: ['Admin User'] },
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private storage: AppStorageService,
    private permissionsService: NgxPermissionsService,
    private openHelp: OpenHelpDialogService,
    private cbreAlertService: CbreAlertService,
    private router: Router,

    appVersionUpdateService: AppVersionUpdateService,
    private mapsScriptsService: MapsScriptsService,
    userInfoResolver: UserInfoResolver,
    @Inject(CbreLoggerToken) private logger: CbreLogger,
    @Inject(FEATURE_TOGGLER) private featureToggler: HybridFeatureTogglerService,
    alchemerService: AlchemerSurveyService,
    trackSpmtNavigationService: TrackSpmtNavigationService
  ) {
    if (environment.production) {
      appVersionUpdateService.init();
    }

    // Make sure feature flags are loaded before setting up menu items
    this.featureToggler.loadFeatureFlags()?.subscribe(() => {
      // Workflows — DB-backed kill-switch (mirrors WORKFLOWS_ENABLED_TOGGLE in
      // workflows-feature-flag.guard.ts). Inserted first so its index stays
      // adjacent to "Queues" regardless of subsequent flag-driven splices.
      if (this.featureToggler.isFeatureEnabled('workflows_enabled')) {
        this.menuItems.splice(3, 0, this.#workflowsMenuItem);
      }

      // Report Generator
      if (this.featureToggler.isFeatureEnabled('report-generator')) {
        this.menuItems.splice(2, 0, this.#reporMenuItems);
      }

      // Data Stack — show for either the legacy module or the Angular Elements replacement
      if (this.featureToggler.isFeatureEnabled('spmt-module')) {
        this.menuItems.splice(1, 0, {
          name: 'core.spmt',
          icon: 'spmt-module',
          routerLink: ['/data-stack'],
          permissions: ['Data Stack'],
        });
      }
    });

    try {
      alchemerService.init();
    } catch (e) {
      this.logger.warn(e);
    }

    // Initialize SPMT navigation tracking
    trackSpmtNavigationService.init();

    const { firstName, lastName } = userInfoResolver.resolve();
    const groups = userInfoResolver.resolveGroups();

    const userLocaleId = this.storage.getItem<number>(LOCALE_ID);

    try {
      // initialize map client scripts depending on the user's locale ID
      this.mapsScriptsService.initMapScripts(userLocaleId);
    } catch (e) {
      this.cbreAlertService.showInfoSnackBar_l10n('maps.errorLoadingConfigurations');
      this.logger.error(e);
    }

    this.activatedRoute.data
      .pipe(map(data => parseInt(data.length, 10)))
      .subscribe(length => (this.clipboardLength = length));

    this.userName = `${firstName} ${lastName}`;

    this.permissionsService.loadPermissions(groups);

    this.router.events
      .pipe(
        filter(this.filterRouterEvents),
        map(routerEvent => routerEvent instanceof ResolveStart)
      )
      .subscribe(isLoading => (this.loading = isLoading));
  }

  ngOnInit() {
    // Subscribe to the route changes to determine if the app is embedded
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter(
          (event: Record<string, any>) => event instanceof NavigationEnd || event?.routerEvent instanceof NavigationEnd
        ),
        map((event: Record<string, any>) => event?.routerEvent || event)
      )
      .subscribe(e => {
        this.$isEmbedded.set(e.url.includes('report-generator'));
        this.$isClipboard.set(e.url.includes('clipboard'));
      });
  }

  ngOnDestroy() {
    /* required by untilDestroyed */
  }

  async getHelp(): Promise<void> {
    this.disableFlag = true;
    await this.openHelp.getCanvas();
    this.disableFlag = false;
  }

  private filterRouterEvents(routerEvent: Event) {
    return (
      routerEvent instanceof ResolveStart ||
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    );
  }
}
